import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addTimeEntry,
  deleteTimeEntry,
  listTimeEntries,
  updateTimeEntry,
  type AddTimeEntryInput,
} from '../lib/tasksApi';

// A worklog mutation changes the entry list, the task's spentHours (detail +
// list serializers) and the activity timeline ('time_logged' entries).
function invalidate(qc: ReturnType<typeof useQueryClient>, taskId: string) {
  void qc.invalidateQueries({ queryKey: ['time-entries', taskId] });
  void qc.invalidateQueries({ queryKey: ['task', taskId] });
  void qc.invalidateQueries({ queryKey: ['task-timeline', taskId] });
  void qc.invalidateQueries({ queryKey: ['tasks'] });
}

export function useTimeEntries(taskId: string | undefined) {
  return useQuery({
    queryKey: ['time-entries', taskId],
    queryFn: () => listTimeEntries(taskId as string),
    enabled: Boolean(taskId),
  });
}

export function useAddTimeEntry(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddTimeEntryInput) => addTimeEntry(taskId, input),
    onSuccess: () => invalidate(qc, taskId),
  });
}

export function useUpdateTimeEntry(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { entryId: string; hours?: number; date?: string; note?: string | null }) =>
      updateTimeEntry(taskId, input.entryId, {
        hours: input.hours,
        date: input.date,
        note: input.note,
      }),
    onSuccess: () => invalidate(qc, taskId),
  });
}

export function useDeleteTimeEntry(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => deleteTimeEntry(taskId, entryId),
    onSuccess: () => invalidate(qc, taskId),
  });
}
